import React, { useState } from 'react';
import { Upload, FileText, X, AlertCircle } from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';
const MAX_FILE_SIZE = 48 * 1024 * 1024; // 48 MB
export function ContactSection() {
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string>('');
  const [dragActive, setDragActive] = useState(false);
  const {
    t
  } = useLanguage();
  const validateFile = (file: File): boolean => {
    setFileError('');
    if (file.size > MAX_FILE_SIZE) {
      setFileError('File size exceeds 48 MB limit');
      return false;
    }
    const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setFileError('Invalid file type. Please upload PDF, DOC, DOCX, XLS, XLSX, or image files');
      return false;
    }
    return true;
  };
  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      if (validateFile(file)) {
        setUploadedFile(file);
      }
    }
  };
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      if (validateFile(file)) {
        setUploadedFile(file);
      }
    }
  };
  const removeFile = () => {
    setUploadedFile(null);
    setFileError('');
  };
  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };
  return <section className="w-full py-16 px-6 md:px-12 bg-[var(--color-bg)]" data-id="element-32">
      <div className="max-w-3xl mx-auto" data-id="element-33">
        <div className="bg-white border border-gray-200 rounded-2xl p-6 md:p-8" data-id="element-34">
          <div className="text-center mb-8" data-id="element-35">
            <h2 className="text-3xl font-bold text-[var(--color-text-dark)] mb-2" data-id="element-36">
              {t('contact.title')}
            </h2>
            <p className="text-[var(--color-text-light)] text-sm" data-id="element-37">
              {t('contact.desc')}
            </p>
          </div>

          <form className="space-y-5" data-id="element-38">
            {/* Row 1 */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-39">
              <div data-id="element-40">
                <label className="block text-sm font-medium text-[var(--color-text-dark)] mb-2" data-id="element-41">
                  {t('contact.labels.company')}{' '}
                  <span className="text-red-500" data-id="element-42">*</span>
                </label>
                <input type="text" className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[var(--color-tan)] transition-colors" data-id="element-43" />
              </div>
              <div data-id="element-44">
                <label className="block text-sm font-medium text-[var(--color-text-dark)] mb-2" data-id="element-45">
                  {t('contact.labels.person')}{' '}
                  <span className="text-red-500" data-id="element-46">*</span>
                </label>
                <input type="text" className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[var(--color-tan)] transition-colors" data-id="element-47" />
              </div>
            </div>

            {/* Row 2 */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-48">
              <div data-id="element-49">
                <label className="block text-sm font-medium text-[var(--color-text-dark)] mb-2" data-id="element-50">
                  {t('contact.labels.email')}{' '}
                  <span className="text-red-500" data-id="element-51">*</span>
                </label>
                <input type="email" className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[var(--color-tan)] transition-colors" data-id="element-52" />
              </div>
              <div data-id="element-53">
                <label className="block text-sm font-medium text-[var(--color-text-dark)] mb-2" data-id="element-54">
                  {t('contact.labels.phone')}{' '}
                  <span className="text-red-500" data-id="element-55">*</span>
                </label>
                <input type="tel" placeholder="+1 234 567 8900" className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[var(--color-tan)] transition-colors placeholder:text-gray-400" data-id="element-56" />
                <p className="text-xs text-[var(--color-text-light)] mt-1" data-id="element-57">
                  {t('contact.labels.phoneHint')}
                </p>
              </div>
            </div>

            {/* Textarea */}
            <div data-id="element-58">
              <label className="block text-sm font-medium text-[var(--color-text-dark)] mb-2" data-id="element-59">
                {t('contact.labels.task')}{' '}
                <span className="text-red-500" data-id="element-60">*</span>
              </label>
              <textarea rows={4} className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[var(--color-tan)] transition-colors resize-none placeholder:text-gray-400" data-id="element-61" />
            </div>

            {/* Project type */}
            <div data-id="element-62">
              <label className="block text-sm font-medium text-[var(--color-text-dark)] mb-2" data-id="element-63">
                {t('contact.labels.project')}
              </label>
              <input type="text" className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[var(--color-tan)] transition-colors" data-id="element-64" />
            </div>

            {/* File upload */}
            <div data-id="element-65">
              <label className="block text-sm font-medium text-[var(--color-text-dark)] mb-2" data-id="element-66">
                {t('contact.labels.files')}
              </label>

              {!uploadedFile ? <div data-id="element-67">
                  <div onDragEnter={handleDrag} onDragLeave={handleDrag} onDragOver={handleDrag} onDrop={handleDrop} onClick={() => document.getElementById('contact-file-upload')?.click()} className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${dragActive ? 'border-[var(--color-tan)] bg-[var(--color-tan)]/5' : 'border-gray-300 hover:border-gray-400'}`} data-id="element-68">
                    <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" data-id="element-69" />
                    <p className="text-sm text-[var(--color-text-dark)] mb-1" data-id="element-70">
                      {t('contact.labels.dragDrop')}
                    </p>
                    <p className="text-xs text-[var(--color-text-light)]" data-id="element-71">
                      {t('contact.labels.fileTypes')}
                    </p>
                    <input id="contact-file-upload" type="file" accept=".pdf,.doc,.docx,.xls,.xlsx,image/png,image/jpeg,image/jpg,image/gif,image/webp" onChange={handleFileChange} className="hidden" data-id="element-72" />
                  </div>
                  {fileError && <div className="mt-2 flex items-start gap-2 text-red-500 text-sm" data-id="element-73">
                      <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" data-id="element-74" />
                      <span data-id="element-75">{fileError}</span>
                    </div>}
                </div> : <div className="border-2 border-[var(--color-tan)] bg-[var(--color-tan)]/5 rounded-lg p-4 flex items-center justify-between" data-id="element-76">
                  <div className="flex items-center gap-3" data-id="element-77">
                    <FileText className="w-8 h-8 text-[var(--color-tan)]" data-id="element-78" />
                    <div data-id="element-79">
                      <p className="text-sm font-medium text-[var(--color-text-dark)]" data-id="element-80">
                        {uploadedFile.name}
                      </p>
                      <p className="text-xs text-[var(--color-text-light)]" data-id="element-81">
                        {formatFileSize(uploadedFile.size)}
                      </p>
                    </div>
                  </div>
                  <button type="button" onClick={removeFile} className="p-2 hover:bg-red-50 rounded-full transition-colors" data-id="element-82">
                    <X className="w-5 h-5 text-red-500" data-id="element-83" />
                  </button>
                </div>}
            </div>

            {/* Submit button - Centered */}
            <div className="flex justify-center pt-2" data-id="element-84">
              <button type="submit" className="px-8 py-3 bg-[var(--color-tan)] text-white text-sm font-medium rounded-lg hover:opacity-90 transition-opacity" data-id="element-85">
                {t('contact.button')}
              </button>
            </div>

            <p className="text-xs text-[var(--color-text-light)] text-center" data-id="element-86">
              {t('contact.disclaimer')}
            </p>
          </form>
        </div>
      </div>
    </section>;
}